import moment from "moment";
import {findIndex, uniqBy} from "lodash";

function shouldGroupContainTask(group, task) {
    if (!group || group.selector || !task) return false;
    if (group.FarmID && group.FarmID !== task.FarmID) return false;
    if (group.filters?.FarmID && group.filters.FarmID !== task.FarmID)
        return false;
    if (!Object.keys(group.filters || {}).length && group.FarmID) return true;
    return (
        group.name === "all" ||
        group.filters?.Status === task.Status ||
        group.name === task.Status
    );
}

function addTaskToGroupedState(grouped, task) {
    if (!grouped.length || !task) return grouped;

    let changed = false;
    const nextGroups = grouped.map((group) => {
        if (!shouldGroupContainTask(group, task)) return group;
        if (group.Items?.some((item) => item.IssueID === task.IssueID)) {
            return group;
        }
        changed = true;
        return {...group, Items: [...(group.Items || []), task]};
    });

    return changed ? nextGroups : grouped;
}

function removeTaskFromGroupedState(grouped, issueID) {
    if (!grouped.length) return grouped;

    let changed = false;
    const nextGroups = grouped.map((group) => {
        if (!group.Items?.some((item) => item.IssueID === issueID))
            return group;
        changed = true;
        return {
            ...group,
            Items: group.Items.filter((item) => item.IssueID !== issueID),
        };
    });

    return changed ? nextGroups : grouped;
}

function replaceTaskInGroupedState(grouped, previousTask, nextTask) {
    const issueID = previousTask?.IssueID || nextTask?.IssueID;
    const farmChanged =
        !!previousTask && !!nextTask && previousTask.FarmID !== nextTask.FarmID;

    if (farmChanged) {
        const withoutPrevious = removeTaskFromGroupedState(grouped, issueID);
        return addTaskToGroupedState(withoutPrevious, nextTask);
    }

    if (!grouped.length) return grouped;

    let changed = false;
    const nextGroups = grouped.map((group) => {
        const groupItems = group.Items || [];
        const index = findIndex(groupItems, (item) => item.IssueID === issueID);
        const hasTask = index !== -1;
        const shouldContain =
            !!nextTask && shouldGroupContainTask(group, nextTask);
        if (hasTask && shouldContain) {
            const updatedItems = groupItems.slice();
            updatedItems[index] = nextTask;
            changed = true;
            return {...group, Items: updatedItems};
        }

        if (hasTask && !shouldContain) {
            changed = true;
            return {
                ...group,
                Items: groupItems.filter((item) => item.IssueID !== issueID),
            };
        }

        if (!hasTask && shouldContain) {
            changed = true;
            return {...group, Items: [...groupItems, nextTask]};
        }

        return group;
    });

    return changed ? nextGroups : grouped;
}

// function getLocalTasksForGroup(tasks, group) {
//     if (!tasks?.length) return [];
//     return tasks.filter(
//         (task) => task?.local && shouldGroupContainTask(group, task)
//     );
// }

function filterOutRemovedTasks(tasks, removedTaskIds) {
    if (!tasks?.length) return [];
    if (!removedTaskIds?.length) return tasks;
    return tasks.filter((task) => !removedTaskIds.includes(task.IssueID));
}

export const initialValue = {
    tasks: {Items: [], NextToken: null}, // lista zadań
    filters: {DtaPlanned: {value: "any"}, Status: "any"}, // zaznaczone tylko zadania do wykonania
    filterId: null,
    loading: false, // czy pobierane są taskidata
    month: moment.utc().month(), // wybrany miesiąc dla kalendarza
    year: moment.utc().year(), // wybrany rok dla kalendarza
    loadingNewerData: false,
    NextTokenTop: null,
    NextTokenBottom: null,
    files: {},
    grouped: [],
    color: "project",
    aggregation: "DtaPlanned",
    sort: "ASC",
    search: undefined,
    openingSearch: false,
    bookmarkSettingModal: false,
    selectedBookmark: null,
    createTaskModal: false,
    removedTaskIds: [],
    openedTask: null,
    openedTaskLoading: false,
};

export default function taskReducer(state = initialValue, action) {
    switch (action.type) {
        case "SET_AGGREGATION":
            return {...state, aggregation: action.payload, filterId: null};
        case "SET_SORT":
            return {...state, sort: action.payload, filterId: null};
        case "SET_FILTERS":
            return {...state, filters: action.payload, filterId: null};
        case "SET_TASK_FILTER_ID":
            return {...state, filterId: action.payload};
        case "SET_SEARCH":
            return {...state, search: action.payload};
        case "SET_OPENING_SEARCH":
            return {...state, openingSearch: action.payload};
        case "SET_BOOKMARK_SETTING_MODAL":
            return {...state, bookmarkSettingModal: action.payload};
        case "SELECT_BOOKMARK":
            return {...state, selectedBookmark: action.payload};
        case "SET_TASK_LOADING":
            return {...state, loading: action.payload};
        case "GET_TASKS_PENDING":
            return {
                ...state,
                loading: action.meta.setLoading,
                loadingNewerData: action.meta.keepExisting,
            };
        case "GET_TASKS_FULFILLED": {
            let tasks = action.meta.keepExisting
                ? action.meta.scroll === "top"
                    ? [...action.payload.Items.reverse(), ...state.tasks.Items]
                    : [...state.tasks.Items, ...action.payload.Items]
                : action.payload.Items;
            return {
                ...state,
                tasks: {...action.payload, Items: tasks},
                loading: false,
                loadingNewerData: false,
                NextTokenTop:
                    action.meta.scroll === "top"
                        ? action.payload.NextToken
                        : state.NextTokenTop,
                NextTokenBottom:
                    action.meta.scroll === "bottom"
                        ? action.payload.NextToken
                        : state.NextTokenBottom,
            };
        }
        case "GET_TECHNOLOGYGROUP_TASKS_FULFILLED": {
            let tasks = action.payload.Items;
            return {
                ...state,
                technologyGroupTasks: {...action.payload, Items: tasks},
            };
        }
        case "GET_TASKS_REJECTED":
            return {
                ...state,
                tasks: {Items: [], NextToken: null},
                loading: action.payload.name === "AbortError",
                loadingNewerData: false,
            };
        case "CHANGE_TASK_MONTH_AND_YEAR":
            return {
                ...state,
                month: action.payload.month,
                year: action.payload.year,
            };
        case "SET_TASK_STATUS": {
            const index = findIndex(
                state.tasks.Items,
                (o) => o.IssueID === action.meta.IssueID
            );
            const copy = state.tasks.Items.slice();
            copy[index] = {...copy[index], Status: action.payload};
            const obj = {
                tasks: {...state.tasks, Items: copy},
            };
            if (state.grouped.length > 0) {
                const groups = state.grouped.slice();
                for (let i = 0; i < groups.length; i++) {
                    const group = groups[i];
                    const index = findIndex(
                        group.Items,
                        (o) => o.IssueID === action.meta.IssueID
                    );
                    if (index !== -1) {
                        const copy = group.Items.slice();
                        copy[index] = {...copy[index], Status: action.payload};
                        groups[i] = {...groups[i], Items: copy};
                        obj.grouped = groups;
                        break;
                    }
                }
            }
            return {
                ...state,
                ...obj,
            };
        }
        case "GET_TASK_TO_DRAWER_PENDING": {
            // re-fetch tego samego zadania (np. ponowne otwarcie tuz po
            // zapisie) - nie zerujemy openedTask, bo pobrany wynik moze byc
            // z niezaindeksowanej jeszcze wyszukiwarki (starszy niz to co juz
            // mamy lokalnie, patrz FULFILLED). Zerujemy tylko przy przejsciu
            // na inne zadanie, zeby nie mrugnac tresc poprzedniego.
            const sameTask = state.openedTask?.IssueID === action.meta?.IssueID;
            return {
                ...state,
                openedTaskLoading: true,
                openedTask: sameTask ? state.openedTask : null,
            };
        }
        case "GET_TASK_TO_DRAWER_FULFILLED": {
            const fetched = action.payload?.Items?.[0] || null;
            const isStaleRead =
                fetched &&
                state.openedTask?.IssueID === fetched.IssueID &&
                state.openedTask.DtaModTime > fetched.DtaModTime;
            return {
                ...state,
                openedTaskLoading: false,
                openedTask: isStaleRead ? state.openedTask : fetched,
            };
        }
        case "GET_TASK_TO_DRAWER_REJECTED": {
            const sameTask = state.openedTask?.IssueID === action.meta?.IssueID;
            return {
                ...state,
                openedTaskLoading: false,
                openedTask: sameTask ? state.openedTask : null,
            };
        }
        case "CLEAR_TASKS":
            return {
                ...state,
                tasks: initialValue.tasks,
                openedTask: null,
                openedTaskLoading: false,
                NextTokenTop: null,
                NextTokenBottom: null,
                removedTaskIds: [],
            };
        case "GET_TASKS_WITH_REPLACE_FULFILLED": {
            let tasks = state.tasks.Items.slice(0);
            for (let task of action.payload.Items) {
                if (state.removedTaskIds.includes(task.IssueID)) continue;
                let index = findIndex(tasks, (o) => o.IssueID === task.IssueID);
                if (index === -1) {
                    tasks.push(task);
                } else if (task.DtaModTime > tasks[index].DtaModTime) {
                    tasks[index] = task;
                }
            }
            tasks = tasks.filter(
                (item) =>
                    item.local ||
                    item.DtaPlanned !== action.meta.DtaPlanned ||
                    (action.meta.FarmID &&
                        item.FarmID !== action.meta.FarmID) ||
                    !!action.payload.Items.find(
                        (subItem) => subItem.IssueID === item.IssueID
                    )
            );
            return {...state, tasks: {...state.tasks, Items: tasks}};
        }
        case "ADD_TASK": {
            const items = state.tasks.Items.slice();
            items.push(action.payload);
            return {
                ...state,
                tasks: {...state.tasks, Items: items},
                grouped: addTaskToGroupedState(state.grouped, action.payload),
            };
        }
        case "REMOVE_TASK": {
            const tasks = state.tasks.Items.filter(
                (item) => item.IssueID !== action.payload
            );
            return {
                ...state,
                tasks: {...state.tasks, Items: tasks},
                grouped: removeTaskFromGroupedState(
                    state.grouped,
                    action.payload
                ),
                removedTaskIds: [...state.removedTaskIds, action.payload],
                // otwarty drawer czyta z osobnego state.openedTask - bez tego
                // modal usunietego zadania zostawal otwarty (zob. ADD_TASK_COMMENT)
                openedTask:
                    state.openedTask?.IssueID === action.payload
                        ? null
                        : state.openedTask,
            };
        }

        case "EDIT_TASK": {
            const tasks = state.tasks.Items.slice();
            let index = findIndex(
                tasks,
                (o) => o.IssueID === action.payload.IssueID
            );
            const previousTask = index !== -1 ? tasks[index] : null;

            if (index === -1) {
                tasks.push(action.payload);
            } else {
                tasks[index] = action.payload;
            }
            return {
                ...state,
                tasks: {...state.tasks, Items: tasks},
                grouped: replaceTaskInGroupedState(
                    state.grouped,
                    previousTask,
                    action.payload
                ),
                // otwarty drawer czyta z osobnego state.openedTask - bez tego
                // po zapisie edycji drawer nadal pokazywal stara wersje zadania
                openedTask:
                    state.openedTask?.IssueID === action.payload.IssueID
                        ? action.payload
                        : state.openedTask,
            };
        }
        case "ADD_TASK_COMMENT": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                const task = JSON.parse(JSON.stringify(clone[index]));
                if (!task.Comments) task.Comments = [];
                task.Comments.push(action.payload);
                task.DtaModTime = action.payload.Date;
                clone[index] = task;
                // otwarty drawer czyta z osobnego state.openedTask (patrz GET_TASK_TO_DRAWER_FULFILLED),
                // nie z tasks.Items - bez tego komentarz pojawiał się dopiero po ponownym otwarciu modala
                const openedTask =
                    state.openedTask?.IssueID === action.meta.issueID
                        ? JSON.parse(JSON.stringify(task))
                        : state.openedTask;
                return {
                    ...state,
                    tasks: {...state.tasks, Items: clone},
                    openedTask,
                };
            }
            return state;
        }
        case "SET_TASK_COMMENT_FILES": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                if (index === -1) return state;
                const task = JSON.parse(JSON.stringify(clone[index]));
                const comment = (task.Comments || []).find(
                    (item) => item.Date === action.payload.Date
                );
                if (!comment) return state;
                comment.Files = action.payload.Files;
                clone[index] = task;
                return {...state, tasks: {...state.tasks, Items: clone}};
            }
            return state;
        }
        case "REMOVE_TASK_COMMENT": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                const task = JSON.parse(JSON.stringify(clone[index]));
                if (task.Comments) {
                    task.Comments.splice(action.payload.index, 1);
                }
                task.DtaModTime = +new Date();
                clone[index] = task;
                const openedTask =
                    state.openedTask?.IssueID === action.meta.issueID
                        ? JSON.parse(JSON.stringify(task))
                        : state.openedTask;
                return {
                    ...state,
                    tasks: {...state.tasks, Items: clone},
                    openedTask,
                };
            }
            return state;
        }
        case "EDIT_TASK_COMMENT": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                const task = JSON.parse(JSON.stringify(clone[index]));
                if (
                    task.Comments &&
                    task.Comments[action.payload.index] !== undefined
                ) {
                    task.Comments[action.payload.index].Text =
                        action.payload.comment;
                }
                task.DtaModTime = +new Date();
                clone[index] = task;
                const openedTask =
                    state.openedTask?.IssueID === action.meta.issueID
                        ? JSON.parse(JSON.stringify(task))
                        : state.openedTask;
                return {
                    ...state,
                    tasks: {...state.tasks, Items: clone},
                    openedTask,
                };
            }
            return state;
        }
        case "ADD_TEMPORARY_TASK": {
            const items = state.tasks.Items.slice();
            items.push(action.payload);
            items.sort((a, b) => a.DtaPlanned - b.DtaPlanned);
            return {...state, tasks: {...state.tasks, Items: items}};
        }
        case "REMOVE_TASK_FROM_LIST": {
            const items = state.tasks.Items.slice();
            const index = findIndex(
                items,
                (o) => o.IssueID === action.payload.id
            );
            if (index !== -1) {
                items.splice(index, 1);
                return {
                    ...state,
                    tasks: {...state.tasks, Items: items},
                    grouped: removeTaskFromGroupedState(
                        state.grouped,
                        action.payload.id
                    ),
                    removedTaskIds: state.removedTaskIds.includes(
                        action.payload.id
                    )
                        ? state.removedTaskIds
                        : [...state.removedTaskIds, action.payload.id],
                    openedTask:
                        state.openedTask?.IssueID === action.payload.id
                            ? null
                            : state.openedTask,
                };
            }
            return state;
        }
        case "START_TASK_FILE_UPLOAD": {
            return {
                ...state,
                files: {
                    ...state.files,
                    [action.meta.IssueID]: {
                        ...(state.files[action.meta.IssueID] || {}),
                        [action.meta.FileID]: {
                            current: 0,
                            size: action.payload.size,
                            file: action.payload.file,
                        },
                    },
                },
            };
        }
        case "TASK_FILE_UPLOAD_PROGRESS": {
            return {
                ...state,
                files: {
                    ...state.files,
                    [action.meta.IssueID]: {
                        ...state.files[action.meta.IssueID],
                        [action.meta.FileID]: {
                            ...state.files[action.meta.IssueID][
                                action.meta.FileID
                            ],
                            current: action.payload.loaded,
                        },
                    },
                },
            };
        }
        case "TASK_FILE_UPLOAD_REMOVE": {
            if (!state.files[action.meta.IssueID]) return state;
            const issueObject = {...(state.files[action.meta.IssueID] || {})};
            delete issueObject[action.meta.FileID];
            return {
                ...state,
                files: {...state.files, [action.meta.IssueID]: issueObject},
            };
        }
        case "GET_GROUPED_TASKS_PENDING": {
            return {
                ...state,
                loading: action.meta.isLoading ? action.meta.replace : false,
                loadingNewerData: !action.meta.replace,
            };
        }
        case "GET_GROUPED_TASKS_FULFILLED": {
            if (!action.meta.replace) {
                const groups = state.grouped.slice();
                const tasks = state.tasks.Items.slice();
                for (let i = 0; i < action.meta.groupNames.length; i++) {
                    const row = action.meta.groupNames[i];
                    const index = groups.findIndex(
                        (item) => item.name === row.name
                    );
                    const incomingItems = filterOutRemovedTasks(
                        action.payload[i]?.Items || [],
                        state.removedTaskIds
                    );
                    const Items = groups[index]?.Items
                        ? [...groups[index]?.Items, ...incomingItems]
                        : incomingItems;
                    groups[index] = {
                        ...groups[index],
                        ...row,
                        ...action.payload[i],
                        NextToken: action.payload[i].NextToken,
                        Items,
                    };
                    tasks.push(...incomingItems);
                }
                return {
                    ...state,
                    grouped: groups,
                    loadingNewerData: false,
                    tasks: {...state.tasks, Items: tasks},
                };
            } else {
                const oldMap = new Map();
                const oldTasks = state.tasks.Items.slice();
                for (const task of oldTasks) {
                    oldMap.set(task.IssueID, task);
                }
                const groups = [];
                const tasks = [];
                for (let i = 0; i < action.meta.groupNames.length; i++) {
                    const row = action.meta.groupNames[i];
                    const oldGroup = state.grouped.find(
                        (item) => item.name === row.name
                    );

                    const items = filterOutRemovedTasks(
                        action.payload[i] ? action.payload[i].Items : [],
                        state.removedTaskIds
                    );

                    // Deduplicate tasks within the group - keep only the one with latest DtaModTime
                    const deduplicatedItems = [];
                    const taskMap = new Map();

                    for (const task of items) {
                        const existingTask = oldMap.get(task.IssueID);
                        if (!existingTask) {
                            taskMap.set(task.IssueID, task);
                        } else {
                            // If existing task is local, API response always overwrites it
                            if (existingTask.local) {
                                taskMap.set(task.IssueID, task);
                            } else if (
                                task.DtaModTime > existingTask.DtaModTime
                            ) {
                                taskMap.set(task.IssueID, task);
                            } else {
                                taskMap.set(existingTask.IssueID, existingTask);
                            }
                        }
                    }
                    deduplicatedItems.push(...taskMap.values());
                    const groupItems = deduplicatedItems.slice(
                        0,
                        action.meta.maxItems
                    );
                    groups.push({
                        ...row,
                        show:
                            !action.meta.clearCurrent && oldGroup
                                ? oldGroup.show
                                : row.hasOwnProperty("show")
                                  ? action.meta.clearCurrent
                                      ? row.show
                                      : state.grouped[i].show
                                  : groupItems.length
                                    ? true
                                    : false,
                        ...action.payload[i],
                        Items: groupItems,
                    });

                    tasks.push(...deduplicatedItems);
                }
                return {
                    ...state,
                    loading: false,
                    grouped: groups,
                    tasks: {...state.tasks, Items: uniqBy(tasks, "IssueID")},
                };
            }
        }
        case "CHANGE_TASK_GROUP_SHOW_STATUS": {
            const groupIndex = state.grouped.findIndex(
                (item) => item.name === action.payload.name
            );
            if (groupIndex === -1) return state;
            const copy = state.grouped.slice();
            copy[groupIndex] = {
                ...copy[groupIndex],
                show: !copy[groupIndex].show,
            };
            return {...state, grouped: copy};
        }
        case "GET_TASKS_FOR_PROJECTS_FULFILLED": {
            const tasks = action.payload?.Items || [];
            return {...state, tasks: {...state.tasks, Items: tasks}};
        }
        case "CHANGE_COLOR_METHOD": {
            return {...state, color: action.payload};
        }
        case "CLEAR_GROUPS": {
            return {...state, grouped: []};
        }
        case "ADD_TASK_TO_DO_ITEM": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                const task = JSON.parse(JSON.stringify(clone[index]));
                task.DtaModTime = +new Date();
                if (!task.ListTODO) task.ListTODO = [];
                task.ListTODO.push(action.payload);
                clone[index] = task;
                return {...state, tasks: {...state.tasks, Items: clone}};
            }
            return state;
        }
        case "REMOVE_TASK_TO_DO_ITEM": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                const task = JSON.parse(JSON.stringify(clone[index]));
                task.DtaModTime = +new Date();
                const itemIndex = findIndex(
                    task.ListTODO,
                    (o) => o.Text === action.meta.Text
                );
                task.ListTODO.splice(itemIndex, 1);
                clone[index] = task;
                return {...state, tasks: {...state.tasks, Items: clone}};
            }
            return state;
        }
        case "SET_TO_DO_ITEM_STATUS": {
            if (action.meta.issueID) {
                const clone = state.tasks.Items.slice();
                const index = findIndex(
                    clone,
                    (o) => o.IssueID === action.meta.issueID
                );
                const task = JSON.parse(JSON.stringify(clone[index]));
                task.DtaModTime = +new Date();
                const itemIndex = findIndex(
                    task.ListTODO,
                    (o) => o.ListID === action.meta.listID
                );
                task.ListTODO[itemIndex].Done = action.payload.Done;
                clone[index] = task;
                return {...state, tasks: {...state.tasks, Items: clone}};
            }
            return state;
        }
        case "CHANGE_FARM":
            return initialValue;
        default:
            return state;
    }
}
